import React, { useState, useCallback, useEffect, useMemo, Fragment } from 'react';
import isEqual from 'lodash/isEqual';
import { jwtDecode } from 'jwt-decode';

// @mui
import {
  Card,
  Table,
  Button,
  Tooltip,
  Container,
  TableBody,
  IconButton,
  TableContainer,
  LinearProgress,
  Box,
  TableRow,
  TableCell,
  Collapse,
  Stack,
  Chip,
  Popover,
  TextField,
  InputAdornment,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Checkbox,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

// routes
import { useParams } from 'src/routes/hook';
import { RouterLink } from 'src/routes/components';

// hooks
import { useBoolean } from 'src/hooks/use-boolean';
// components
import Iconify from 'src/components/iconify';
import Scrollbar from 'src/components/scrollbar';
import { ConfirmDialog } from 'src/components/custom-dialog';
import { useSettingsContext } from 'src/components/settings';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';
import {
  useTable,
  emptyRows,
  TableNoData,
  TableEmptyRows,
  TableHeadCustom,
  TablePaginationCustom,
} from 'src/components/table';
import { GridFilterAltIcon } from '@mui/x-data-grid';

import { usePaymentActions } from 'src/context/providers/PaymentContext';
import PropTypes from 'prop-types';
import { useOrderActions, useOrderData } from 'src/context/providers/OrderContext';
import GroupUpdateModal from './group-update';
import PaymentDrawer from './payment-drawer';

import OrderDrawerComponent from './order-drawer-component';
import OrderCreation from './order-creation';
import DirectionPickerModal from './direction-picker-modal';
import PrintLabel from './PrintLabel';
import { filterKeys } from './view/data';
import { apiService } from '../../utils/apiServices';
import { PHYSICAL_API, destinationDeliveryOf } from '../../utils/physical-api';

// Small helpers
const fmt = (v) => (v == null ? '-' : String(v));

const auth = JSON.parse(localStorage.getItem('token'));
const decoded = auth ? jwtDecode(auth) : null;

export default function TcTable({
  STATUS_OPTIONS, // not used here but kept for API parity
  TABLE_HEAD, // not used here but kept for API parity
  defaultFilters,
  tableNames, // not used here but kept for API parity
  OTHER_STATUS_OPTIONS_FOR_ORDER,
  TIME_SLOT_FOR_ORDER, // not used here but kept for API parity
}) {
  const table = useTable();
  const settings = useSettingsContext();

  const {
    deletePhysicalOrder,
    getOrderssPhysical,
    getPhysicalOrderById,
    updateGroup,
    getCustomFeeSum,
    payCustomFees,
    getQuickshipperExcelData,
  } = useOrderActions();
  const { initiateGroupPayment } = usePaymentActions();

  const tableData = useOrderData().physicalOrders;
  const tableDetails = useOrderData().physicalOrdersDetails;

  const groups = useMemo(
    () => (Array.isArray(tableData) ? tableData : tableData?.data || []),
    [tableData]
  );

  const [aRow, setARow] = useState({});

  const confirm = useBoolean();
  const { order_id } = useParams();

  useEffect(() => {
    if (!order_id) return;
    getPhysicalOrderById(order_id)
      .then((fetchedOrder) => {
        if (fetchedOrder) {
          setARow(fetchedOrder);
          setIsDrawerOpen(true);
        }
      })
      .catch((err) => console.error('Error fetching order by ID:', err));
  }, [order_id, getPhysicalOrderById]);

  // filters
  const [filters, setFilters] = useState({ ...defaultFilters });
  const [selectedGroupId, setSelectedGroupId] = useState(filters.group_id || '');
  const [selectedStatus, setSelectedStatus] = useState(filters.status || '');
  const [selectedCustomStatus, setSelectedCustomStatus] = useState(filters.custom_status || '');
  const [selectedRsPayByGz, setSelectedRsPayByGz] = useState(
    filters.DeclarationPaymentAuto !== undefined ? String(filters.DeclarationPaymentAuto) : ''
  );
  const [selectedDeliveryMethod, setSelectedDeliveryMethod] = useState(
    filters.deliveryExtraMethod || ''
  );
  const [selectedGroupsForPayment, setSelectedGroupsForPayment] = useState({
    primaryId: null,
    groups: [],
  });

  // Sync filter states with filters when filters change externally
  useEffect(() => {
    setSelectedStatus(filters.status || '');
    setSearchName(filters.name || '');
    setSelectedCustomStatus(filters.custom_status || '');
    setSelectedRsPayByGz(
      filters.DeclarationPaymentAuto !== undefined ? String(filters.DeclarationPaymentAuto) : ''
    );
    setSelectedDeliveryMethod(filters.deliveryExtraMethod || '');
  }, [
    filters.status,
    filters.name,
    filters.custom_status,
    filters.DeclarationPaymentAuto,
    filters.deliveryExtraMethod,
  ]);

  const denseHeight = table.dense ? 52 : 72;
  const canReset = !isEqual(defaultFilters, filters);
  const notFound = (!groups.length && canReset) || !groups.length;
  const [pendingDeleteId, setPendingDeleteId] = useState(null);

  const handleDeleteRow = useCallback(
    (e, id) => {
      e.stopPropagation();
      setPendingDeleteId(id);
      confirm.onTrue();
    },
    [confirm]
  );

  // drawers
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isOrderDrawerOpen, setIsOrderDrawerOpen] = useState(false);
  const handleDrawerToggle = () => setIsDrawerOpen((s) => !s);
  const handleOrderDrawerToggle = () => setIsOrderDrawerOpen((s) => !s);
  const [isDirectionPickerOpen, setIsDirectionPickerOpen] = useState(false);
  const [newOrderDirection, setNewOrderDirection] = useState(null);
  const handleDirectionSelect = (direction) => {
    setNewOrderDirection(direction);
    setIsDirectionPickerOpen(false);
    setIsOrderDrawerOpen(true);
  };
  const handleSelectGroupForPayment = (s, bucket) => {
    const primaryId = s.primary.id;
    const groupId = bucket?.groupId;

    setSelectedGroupsForPayment((prev) => {
      if (prev.primaryId !== primaryId) {
        return { primaryId, groups: [bucket] };
      }

      const isSelected = prev.groups.some((g) => g?.groupId === groupId);
      const grps = isSelected
        ? prev.groups.filter((g) => g?.groupId !== groupId)
        : [...prev.groups, bucket];

      if (grps.length === 0) {
        return { primaryId: null, groups: [] };
      }

      return { primaryId, groups: grps };
    });
  };

  // Print label state
  const [printLabelOpen, setPrintLabelOpen] = useState(false);
  const [orderToPrint, setOrderToPrint] = useState(null);
  const [ordersToPrint, setOrdersToPrint] = useState([]);

  // Selected orders state for bulk printing
  const [selectedOrders, setSelectedOrders] = useState(new Set());

  const handlePrintLabel = (order) => {
    setOrderToPrint(order);
    setOrdersToPrint([]);
    setPrintLabelOpen(true);
  };

  const handlePrintMultiple = (orders) => {
    setOrdersToPrint(orders);
    setOrderToPrint(null);
    setPrintLabelOpen(true);
  };

  const handleClosePrintLabel = () => {
    setPrintLabelOpen(false);
    setOrderToPrint(null);
    setOrdersToPrint([]);
    setSelectedOrders(new Set());
  };

  // Handle order selection
  const handleSelectOrder = (orderId) => {
    setSelectedOrders((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(orderId)) {
        newSet.delete(orderId);
      } else {
        newSet.add(orderId);
      }
      return newSet;
    });
  };

  const handleSelectAllInBucket = (bucketOrders) => {
    const allSelected = bucketOrders.every((order) => selectedOrders.has(order.id));
    if (allSelected) {
      // Deselect all in bucket
      setSelectedOrders((prev) => {
        const newSet = new Set(prev);
        bucketOrders.forEach((order) => newSet.delete(order.id));
        return newSet;
      });
    } else {
      // Select all in bucket
      setSelectedOrders((prev) => {
        const newSet = new Set(prev);
        bucketOrders.forEach((order) => newSet.add(order.id));
        return newSet;
      });
    }
  };

  const getSelectedOrdersInBucket = (bucketOrders) =>
    bucketOrders.filter((order) => selectedOrders.has(order.id));

  // fetch grouped physical orders
  useEffect(() => {
    getOrderssPhysical(filters);
  }, [filters, getOrderssPhysical]);

  // Collapsible groups open state
  const [openGroups, setOpenGroups] = useState({});
  const toggleGroup = (id) => setOpenGroups((prev) => ({ ...prev, [id]: !prev[id] }));

  const [openGroupBuckets, setOpenGroupBuckets] = useState({});
  const toggleBucket = (key) => setOpenGroupBuckets((prev) => ({ ...prev, [key]: !prev[key] }));
  // handlers to open the details drawer for an order
  const openOrderDrawer = async (order) => {
    try {
      setARow(await getPhysicalOrderById(order.id));
      setIsDrawerOpen(true);
    } catch (error) {
      toast.error('Could not load order details');
    }
  };

  // search inputs
  const [searchName, setSearchName] = useState(filters.name ?? '');

  // popover anchor
  const [filterAnchor, setFilterAnchor] = useState(null);
  const filterOpen = Boolean(filterAnchor);
  const openFilter = (e) => setFilterAnchor(e.currentTarget);
  const closeFilter = () => setFilterAnchor(null);
  // inside TcTable component:
  const [editOpen, setEditOpen] = useState(false);
  const [editingBucket, setEditingBucket] = useState(null);
  const [saving, setSaving] = useState(false);

  // Payment drawer state
  const [paymentDrawerOpen, setPaymentDrawerOpen] = useState(false);
  const [paymentBucket, setPaymentBucket] = useState(null);
  const [paymentLoading, setPaymentLoading] = useState(false);

  // Custom fee dialog state
  const [customFeeDialogOpen, setCustomFeeDialogOpen] = useState(false);
  const [customFeeData, setCustomFeeData] = useState(null);
  const [customFeeLoading, setCustomFeeLoading] = useState(false);
  const [customFeePaidData, setCustomFeePaidData] = useState({}); // Store paid custom fee amounts by groupId
  const [currentGroupId, setCurrentGroupId] = useState(null); // Track which group's custom fees are being viewed
  const [payingCustomFee, setPayingCustomFee] = useState(false);

  const handleEdit = (bucket) => {
    setEditingBucket(bucket);
    setEditOpen(true);
  };

  const handlePaymentView = (buckets) => {
    setPaymentBucket(buckets);
    setPaymentDrawerOpen(true);
  };

  const handlePaymentSubmit = async (paymentData) => {
    try {
      setPaymentLoading(true);
      const response = await initiateGroupPayment({
        group_ids: paymentData.buckets.map(({groupId}) => groupId),
        currency: paymentData.currency || 'USD',
        paymentMethod: paymentData.paymentMethod,
      });

      // Log API response for debugging
      // Close drawer and refresh data
      setPaymentDrawerOpen(false);
      await getOrderssPhysical(filters);

      // Success handled inside action via toast
      return response;
    } catch (error) {
      console.error('Error processing payment:', error);
      // You can add error notification here
      return null;
    } finally {
      setPaymentLoading(false);
    }
  };

  const handleTerminalCancel = async () => {
    // Refresh data after a successful terminal cancel
    await getOrderssPhysical(filters);
  };

  const handleCustomFeeClick = async (groupId) => {
    if (!groupId) return;

    try {
      setCustomFeeLoading(true);
      setCurrentGroupId(groupId);
      const data = await getCustomFeeSum(groupId);
      if (data) {
        setCustomFeeData(data);
        setCustomFeeDialogOpen(true);
      }
    } catch (error) {
      console.error('Error fetching custom fee:', error);
    } finally {
      setCustomFeeLoading(false);
    }
  };

  const handlePayCustomFees = async () => {
    if (!currentGroupId) return;

    try {
      setPayingCustomFee(true);
      const result = await payCustomFees(currentGroupId);
      if (result) {
        // Refresh orders and close dialog
        await getOrderssPhysical(filters);
        setCustomFeeDialogOpen(false);
        setCustomFeeData(null);
        setCurrentGroupId(null);
      }
    } catch (error) {
      console.error('Error paying custom fees:', error);
    } finally {
      setPayingCustomFee(false);
    }
  };

  // Check if all custom fees are paid for a bucket
  const areAllCustomFeesPaid = useCallback((bucket) => {
    if (!bucket?.orders) return false;

    const ordersNeedingCustom = bucket.orders.filter((order) => order.NeedsCustom === true);
    if (ordersNeedingCustom.length === 0) return false;

    // Check if all orders with custom fees have them paid
    return ordersNeedingCustom.every((order) => order.custom_fee?.custum_fee_payed === true);
  }, []);

  // Fetch paid custom fees for groups where all custom fees are paid
  useEffect(() => {
    if (!groups.length) return;

    const fetchPaidFees = async () => {
      const groupsToFetch = [];

      groups.forEach((group) => {
        if (Array.isArray(group.groups)) {
          group.groups.forEach((bucket) => {
            const groupId = bucket.group?.id || bucket.groupId;
            if (groupId && areAllCustomFeesPaid(bucket)) {
              groupsToFetch.push({ groupId });
            }
          });
        }
      });

      // Filter out groups we already have data for
      const groupsToFetchFiltered = groupsToFetch.filter(
        ({ groupId }) => !customFeePaidData[groupId]
      );

      // Fetch all paid custom fees
      if (groupsToFetchFiltered.length > 0) {
        await Promise.all(
          groupsToFetchFiltered.map(async ({ groupId }) => {
            try {
              const data = await getCustomFeeSum(groupId);
              if (data) {
                setCustomFeePaidData((prev) => ({ ...prev, [groupId]: data.totalSum }));
              }
            } catch (error) {
              console.error('Error fetching paid custom fee:', error);
            }
          })
        );
      }
    };

    fetchPaidFees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups, areAllCustomFeesPaid, getCustomFeeSum]);

  // call your API here; replace with your real action
  // inside TcTable
  const saveGroupUpdate = async ({ groupId, data }) => {
    try {
      setSaving(true);
      const ok = await updateGroup(groupId, data);
      if (ok) {
        await getOrderssPhysical(filters);
        setEditOpen(false);
      }
    } finally {
      setSaving(false);
    }
  };

  // apply / reset
  const applyFilters = () => {
    const newFilters = { ...filters, page: 1 };

    // Only add name if it has a value, otherwise remove it
    if (searchName && searchName.trim()) {
      newFilters.name = searchName.trim();
    } else {
      delete newFilters.name;
    }

    // Only add status if it has a value, otherwise remove it
    if (selectedStatus && selectedStatus.trim()) {
      newFilters.status = selectedStatus;
    } else {
      delete newFilters.status;
    }

    // Only add custom_status if it has a value, otherwise remove it
    if (selectedCustomStatus && selectedCustomStatus.trim()) {
      newFilters.custom_status = selectedCustomStatus;
    } else {
      delete newFilters.custom_status;
    }

    // Only add DeclarationPaymentAuto if it has a value, otherwise remove it
    if (selectedRsPayByGz && selectedRsPayByGz.trim()) {
      newFilters.DeclarationPaymentAuto = selectedRsPayByGz === 'true';
    } else {
      delete newFilters.DeclarationPaymentAuto;
    }

    // Only add deliveryExtraMethod if it has a value, otherwise remove it
    if (selectedDeliveryMethod && selectedDeliveryMethod.trim()) {
      newFilters.deliveryExtraMethod = selectedDeliveryMethod;
    } else {
      delete newFilters.deliveryExtraMethod;
    }

    // Only add group_id if it has a value, otherwise remove it
    if (selectedGroupId && selectedGroupId.trim()) {
      newFilters.group_id = selectedGroupId;
    } else {
      delete newFilters.group_id;
    }

    setFilters(newFilters);
    closeFilter();
  };

  const resetFilters = () => {
    setSearchName('');
    setSelectedStatus('');
    setSelectedCustomStatus('');
    setSelectedRsPayByGz('');
    setSelectedDeliveryMethod('');
    setFilters(defaultFilters);
    closeFilter();
  };

  const handleDownloadQuickshipperExcel = async () => {
    try {
      const data = await getQuickshipperExcelData();
      if (!data || data.length === 0) {
        toast.warn('No data available for download');
        return;
      }

      // Map backend property names to display names
      const columnMapping = {
        fromAddress: 'From Address',
        fromAddressComment: 'From Address Comment',
        fromLatitude: 'From Latitude',
        fromLongitude: 'From Longitude',
        fromName: 'From Name',
        fromPhone: 'From Phone',
        toAddress: 'To Address',
        toAddressComment: 'To Address Comment',
        toLatitude: 'To Latitude',
        toLongitude: 'To Longitude',
        toName: 'To Name',
        toPhone: 'To Phone',
        comment: 'Comment',
        courier: 'Courier',
        product: 'Product',
      };

      // Transform data to use display names as keys
      const transformedData = data.map((row) => {
        const newRow = {};
        Object.keys(columnMapping).forEach((key) => {
          newRow[columnMapping[key]] = row[key] ?? '';
        });
        return newRow;
      });

      // Create worksheet with display names as headers
      const ws = XLSX.utils.json_to_sheet(transformedData);

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Quickshipper Delivery');
      const dateStr = new Date().toISOString().split('T')[0];
      XLSX.writeFile(wb, `quickshipper_delivery_${dateStr}.xlsx`);
      toast.success('File downloaded successfully');
    } catch (error) {
      console.error('Error downloading quickshipper Excel:', error);
      toast.error('Failed to download file');
    }
  };

  const handleDownloadTbilisiWarehouseOrdersExcel = async () => {
    try {
      const { data, headers } = await apiService.get(PHYSICAL_API.ordersTbilisiWarehouseCsv, {
        responseType: 'blob',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });

      let fileName = 'orders.csv';
      const disposition = headers['content-disposition'];

      if (disposition) {
        const match = disposition.match(/filename="?(.+?)"?$/);
        if (match) fileName = match[1] ?? fileName;
      }

      const url = window.URL.createObjectURL(data);
      const a = document.createElement('a');

      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('File downloaded successfully');
    } catch (error) {
      console.error('Error downloading warehouse Excel:', error);
      toast.error('Failed to download file');
    }
  };

  return (
    <div>
      {!groups.length && <LinearProgress color="success" />}

      <Container maxWidth={settings.themeStretch ? false : 'lg'}>
        {Object.keys(aRow).length > 0 && (
          <OrderDrawerComponent
            key={aRow.id}
            isDrawerOpen={isDrawerOpen}
            handleDrawerToggle={handleDrawerToggle}
            aRow={aRow}
            setARow={setARow}
            filters={filters}
          />
        )}

        <DirectionPickerModal
          open={isDirectionPickerOpen}
          onClose={() => setIsDirectionPickerOpen(false)}
          onSelect={handleDirectionSelect}
        />

        <OrderCreation
          isDrawerOpen={isOrderDrawerOpen}
          handleDrawerToggle={handleOrderDrawerToggle}
          direction={newOrderDirection}
        />

        {/* Header: Title + total badge + filter icon + New Order */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: { xs: 3, md: 5 } }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box component="h2" sx={{ m: 0, fontWeight: 700, fontSize: '1.6rem' }}>
              Order List
            </Box>

            {tableDetails?.total ? (
              <Box
                sx={{
                  ml: 0.5,
                  px: 1,
                  py: 0.25,
                  bgcolor: 'error.light',
                  color: 'common.white',
                  borderRadius: '999px',
                  fontSize: 12,
                  fontWeight: 700,
                }}
              >
                {tableDetails.total}
              </Box>
            ) : null}

            <IconButton size="small" onClick={openFilter} sx={{ ml: 0.5 }}>
              <GridFilterAltIcon />
            </IconButton>

            { (decoded?.location === 'Georgia' && decoded?.role === 'Admin') &&
              <Stack direction='row'>
                <Tooltip title="Tbilisi Delivery">
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleDownloadQuickshipperExcel}
                    startIcon={<Iconify icon="material-symbols:download" />}
                    sx={{ ml: 1 }}
                  >
                    Download Quickshipper
                  </Button>
                </Tooltip>

                <Tooltip title="Region Delivery">
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleDownloadTbilisiWarehouseOrdersExcel}
                    startIcon={<Iconify icon="material-symbols:download" />}
                    sx={{ ml: 1 }}
                  >
                    Download Fedex
                  </Button>
                </Tooltip>
              </Stack>
            }

          </Stack>

          <Button
            component={RouterLink}
            onClick={() => setIsDirectionPickerOpen(true)}
            variant="contained"
            startIcon={<Iconify icon="mingcute:add-line" />}
          >
            New Order
          </Button>
        </Stack>

        <GroupUpdateModal
          open={editOpen}
          onClose={() => setEditOpen(false)}
          bucket={editingBucket}
          onSave={saveGroupUpdate}
          loading={saving}
        />

        <PaymentDrawer
          isDrawerOpen={paymentDrawerOpen}
          handleDrawerToggle={() => setPaymentDrawerOpen(false)}
          buckets={selectedGroupsForPayment.groups}
          onPaymentSubmit={handlePaymentSubmit}
          onTerminalCancel={handleTerminalCancel}
          onReprocessComplete={handleTerminalCancel}
          loading={paymentLoading}
        />

        <PrintLabel
          open={printLabelOpen}
          onClose={handleClosePrintLabel}
          order={orderToPrint}
          orders={ordersToPrint.length > 0 ? ordersToPrint : null}
        />

        {/* Filter popover */}
        <Popover
          open={filterOpen}
          anchorEl={filterAnchor}
          onClose={closeFilter}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          PaperProps={{ sx: { p: 2, width: 300, borderRadius: 2 } }}
        >
          <Box sx={{ mb: 1, fontSize: 12, color: 'text.secondary' }}>Search by</Box>

          <TextField
            fullWidth
            size="small"
            placeholder="Name"
            value={searchName}
            onChange={(e) => setSearchName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') applyFilters();
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Iconify icon="solar:magnifer-linear" />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />

          <Box sx={{ mb: 1, fontSize: 12, color: 'text.secondary' }}>Group Id</Box>

          <TextField
            fullWidth
            size="small"
            placeholder="UUID"
            value={selectedGroupId}
            onChange={(e) => setSelectedGroupId(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') applyFilters();
            }}
            sx={{ mb: 2 }}
          />

          <Box sx={{ mb: 1, fontSize: 12, color: 'text.secondary' }}>Status</Box>

          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={selectedStatus}
              label="Status"
              onChange={(e) => setSelectedStatus(e.target.value)}
            >
              <MenuItem value="">
                <em>All</em>
              </MenuItem>
              {STATUS_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box sx={{ mb: 1, fontSize: 12, color: 'text.secondary' }}>Custom Status</Box>

          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>Custom Status</InputLabel>
            <Select
              value={selectedCustomStatus}
              label="Custom Status"
              onChange={(e) => setSelectedCustomStatus(e.target.value)}
            >
              <MenuItem value="">
                <em>All</em>
              </MenuItem>
              {filterKeys
                .find((filter) => filter.key === 'custom_status')
                ?.options.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>

          <Box sx={{ mb: 1, fontSize: 12, color: 'text.secondary' }}>RS Pay by GZ</Box>

          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>RS Pay by GZ</InputLabel>
            <Select
              value={selectedRsPayByGz}
              label="RS Pay by GZ"
              onChange={(e) => setSelectedRsPayByGz(e.target.value)}
            >
              <MenuItem value="">
                <em>All</em>
              </MenuItem>
              {filterKeys
                .find((filter) => filter.key === 'DeclarationPaymentAuto')
                ?.options.map((option) => (
                  <MenuItem key={String(option.value)} value={String(option.value)}>
                    {option.label}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>

          <Box sx={{ mb: 1, fontSize: 12, color: 'text.secondary' }}>Delivery Method</Box>

          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>Delivery Method</InputLabel>
            <Select
              value={selectedDeliveryMethod}
              label="Delivery Method"
              onChange={(e) => setSelectedDeliveryMethod(e.target.value)}
            >
              <MenuItem value="">
                <em>All</em>
              </MenuItem>
              {filterKeys
                .find((filter) => filter.key === 'deliveryExtraMethod')
                ?.options.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>

          <Divider sx={{ my: 1.5 }} />

          <Stack direction="row" spacing={1.5}>
            <Button variant="contained" onClick={applyFilters}>
              Apply
            </Button>
            <Button variant="text" onClick={resetFilters}>
              Reset
            </Button>
          </Stack>
        </Popover>

        <Card>
          <TableContainer sx={{ position: 'relative', overflow: 'unset' }}>
            <Scrollbar>
              {groups.length ? (
                <Table size={table.dense ? 'small' : 'medium'} sx={{ minWidth: 1100 }}>
                  <TableHeadCustom
                    headLabel={[
                      { id: 'customer', label: 'Customer' },
                      { id: 'primary', label: 'Primary Receiver' },
                      { id: 'ordersCount', label: 'Orders' },
                      { id: 'contact', label: 'Primary Contact' },
                      { id: 'actions', label: '' },
                    ]}
                  />

                  <TableBody>
                    {groups.map((group) => {
                      // eslint-disable-next-line
                      const primary = group.primary;
                      const gid = primary?.id || Math.random().toString(36);
                      const ordersCount = Array.isArray(group.groups)
                        ? group.groups.reduce((sum, b) => sum + (b.orders?.length || 0), 0)
                        : group.orders?.length || 0;

                      // Get customer from first order
                      const firstOrder =
                        Array.isArray(group.groups) && group.groups[0]?.orders?.[0]
                          ? group.groups[0].orders[0]
                          : group.orders?.[0];
                      const customer = firstOrder?.customer;

                      return (
                        <Fragment key={`group-${gid} `}>
                          <TableRow hover>
                            <TableCell sx={{ width: 280 }}>
                              <Stack spacing={0.5}>
                                <Box fontWeight={600}>
                                  {fmt(customer?.first_name)} {fmt(customer?.last_name)}
                                </Box>
                                <Box color="text.secondary">{fmt(customer?.phone)}</Box>
                              </Stack>
                            </TableCell>

                            <TableCell sx={{ width: 520 }}>
                              <Stack spacing={0.5}>
                                <Box fontWeight={600}>
                                  {fmt(primary.first_name)} {fmt(primary.last_name)}
                                </Box>
                                <Stack direction="row" spacing={1} flexWrap="wrap">
                                  <Chip size="small" label={`ID: ${fmt(primary.id)}`} />
                                </Stack>
                              </Stack>
                            </TableCell>

                            <TableCell width={120}>
                              <Chip label={ordersCount} />
                            </TableCell>

                            <TableCell sx={{ minWidth: 320 }}>
                              <Stack spacing={0.5}>
                                <Box>Email: {fmt(primary.email)}</Box>
                                <Box>Phone:{fmt(primary.phone)}</Box>
                              </Stack>
                            </TableCell>

                            <TableCell align="right" sx={{ width: 120 }}>
                              <Stack
                                direction="row"
                                spacing={1}
                                justifyContent="flex-end"
                                alignItems="center"
                              >
                                <Tooltip title="Edit Receiver">
                                  <IconButton size="small" />
                                </Tooltip>
                                <IconButton size="small" onClick={() => toggleGroup(gid)}>
                                  {openGroups[gid] ? (
                                    <KeyboardArrowUpIcon />
                                  ) : (
                                    <KeyboardArrowDownIcon />
                                  )}
                                </IconButton>
                              </Stack>
                            </TableCell>
                          </TableRow>

                          <TableRow>
                            <TableCell colSpan={5} sx={{ py: 0 }}>
                              <Collapse in={!openGroups[gid]} timeout="auto" unmountOnExit>
                                <Button
                                  sx={{ m: 2 }}
                                  variant="contained"
                                  color="primary"
                                  size="small"
                                  onClick={() => handlePaymentView(selectedGroupsForPayment)}
                                  disabled={
                                    !(
                                      selectedGroupsForPayment.primaryId === gid &&
                                      selectedGroupsForPayment.groups.length > 0
                                    )
                                  }
                                >
                                  Pay (
                                  {selectedGroupsForPayment.primaryId === gid
                                    ? selectedGroupsForPayment.groups.length
                                    : 0}
                                  / {group.groups.length})
                                </Button>
                                <Box sx={{ m: 1 }}>
                                  {Array.isArray(group.groups) ? (
                                    group.groups.map((bucket, idx) => {
                                      const bucketKey = `${gid}:${
                                        bucket.groupId || `ungrouped-${idx}`
                                      }`;
                                      const grp = bucket.group || {};

                                      return (
                                        <Box
                                          key={bucketKey}
                                          sx={{
                                            mb: 2,
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            borderRadius: 1.5,
                                          }}
                                        >
                                          {/* Group header */}
                                          <Stack
                                            direction="row"
                                            alignItems="center"
                                            spacing={1}
                                            sx={{ px: 1.5, py: 1 }}
                                          >
                                            <IconButton
                                              size="small"
                                              onClick={() => toggleBucket(bucketKey)}
                                            >
                                              {openGroupBuckets[bucketKey] ? (
                                                <KeyboardArrowUpIcon />
                                              ) : (
                                                <KeyboardArrowDownIcon />
                                              )}
                                            </IconButton>
                                            <Box
                                              sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                width: '100%',
                                              }}
                                            >
                                              <Box
                                                sx={{
                                                  display: 'flex',
                                                  alignItems: 'center',
                                                  gap: 1,
                                                }}
                                              >
                                                <Checkbox
                                                  size="small"
                                                  disabled={
                                                    Boolean(grp?.payed) ||
                                                    grp?.pay_status === 'PAID'
                                                  }
                                                  checked={
                                                    selectedGroupsForPayment.primaryId ===
                                                      group.primary.id &&
                                                    selectedGroupsForPayment.groups.some(
                                                      (g) => g?.groupId === bucket.groupId
                                                    )
                                                  }
                                                  onChange={() =>
                                                    handleSelectGroupForPayment(group, bucket)
                                                  }
                                                />
                                                <Box fontWeight={700}>{`Group ${idx + 1}  - ${
                                                  grp.id
                                                }`}</Box>
                                                <Chip
                                                  size="small"
                                                  label={`${bucket.orders?.length || 0} orders`}
                                                  sx={{ ml: 0.5 }}
                                                />
                                                {grp?.pickup_area && (
                                                  <Chip
                                                    size="small"
                                                    label={`Area: ${grp.pickup_area}`}
                                                  />
                                                )}
                                                {grp?.pickup_price != null && (
                                                  <Chip
                                                    size="small"
                                                    label={`Pick-up: ${grp.pickup_price}`}
                                                  />
                                                )}
                                                {grp?.deliveryExtraMethod && (
                                                  <Chip
                                                    size="small"
                                                    label={`Delivery: ${grp.deliveryExtraMethod.replace(
                                                      /_/g,
                                                      ' '
                                                    )}`}
                                                    color="info"
                                                  />
                                                )}
                                              </Box>

                                              <Box sx={{ display: 'flex', gap: 1 }}>
                                                {(() => {
                                                  const paid =
                                                    Boolean(grp?.payed) ||
                                                    grp?.pay_status === 'PAID';

                                                  // Check if any order in the bucket needs custom
                                                  const hasOrdersNeedingCustom =
                                                    bucket?.orders?.some(
                                                      (order) => order.NeedsCustom === true
                                                    );

                                                  // Check if all custom fees are paid
                                                  const allCustomFeesPaid =
                                                    areAllCustomFeesPaid(bucket);
                                                  const groupId = grp?.id || bucket.groupId;
                                                  const selectedInBucket =
                                                    getSelectedOrdersInBucket(bucket.orders || []);
                                                  const hasSelectedInBucket =
                                                    selectedInBucket.length > 0;

                                                  return (
                                                    <>
                                                      {hasSelectedInBucket && (
                                                        <Button
                                                          variant="contained"
                                                          color="info"
                                                          size="small"
                                                          startIcon={<Iconify icon="mdi:printer" />}
                                                          onClick={() =>
                                                            handlePrintMultiple(selectedInBucket)
                                                          }
                                                        >
                                                          Print Selected ({selectedInBucket.length})
                                                        </Button>
                                                      )}
                                                      {paid && (
                                                        <Button
                                                          variant="contained"
                                                          color="success"
                                                          size="small"
                                                          startIcon={
                                                            <Iconify icon="mdi:check-circle" />
                                                          }
                                                          sx={{
                                                            pointerEvents: 'none',
                                                            opacity: 0.95,
                                                          }}
                                                        >
                                                          Paid
                                                        </Button>
                                                      )}
                                                      {hasOrdersNeedingCustom && (
                                                        <Button
                                                          variant={
                                                            allCustomFeesPaid
                                                              ? 'contained'
                                                              : 'outlined'
                                                          }
                                                          color={
                                                            allCustomFeesPaid ? 'success' : 'info'
                                                          }
                                                          size="small"
                                                          startIcon={
                                                            allCustomFeesPaid ? (
                                                              <Iconify icon="mdi:check-circle" />
                                                            ) : (
                                                              <Iconify icon="mdi:receipt" />
                                                            )
                                                          }
                                                          onClick={() =>
                                                            handleCustomFeeClick(groupId)
                                                          }
                                                          disabled={customFeeLoading}
                                                          sx={
                                                            allCustomFeesPaid
                                                              ? {
                                                                  pointerEvents: 'auto',
                                                                  opacity: 1,
                                                                }
                                                              : undefined
                                                          }
                                                        >
                                                          {allCustomFeesPaid
                                                            ? 'Custom fee Paid'
                                                            : 'Custom Fee'}
                                                        </Button>
                                                      )}
                                                      <Button
                                                        variant="outlined"
                                                        color="secondary"
                                                        size="small"
                                                        onClick={() => handleEdit(bucket)}
                                                      >
                                                        Edit
                                                      </Button>
                                                    </>
                                                  );
                                                })()}
                                              </Box>
                                            </Box>
                                          </Stack>

                                          <Collapse
                                            in={!openGroupBuckets[bucketKey]}
                                            timeout="auto"
                                            unmountOnExit
                                          >
                                            <Divider />
                                            <Box sx={{ p: 1.5 }}>
                                              <Table size="small" sx={{ minWidth: 1000 }}>
                                                <TableHeadCustom
                                                  headLabel={[
                                                    {
                                                      id: 'select',
                                                      label: (
                                                        <Checkbox
                                                          size="small"
                                                          checked={
                                                            (bucket.orders || []).length > 0 &&
                                                            (bucket.orders || []).every((order) =>
                                                              selectedOrders.has(order.id)
                                                            )
                                                          }
                                                          indeterminate={
                                                            (bucket.orders || []).some((order) =>
                                                              selectedOrders.has(order.id)
                                                            ) &&
                                                            !(bucket.orders || []).every((order) =>
                                                              selectedOrders.has(order.id)
                                                            )
                                                          }
                                                          onChange={() =>
                                                            handleSelectAllInBucket(
                                                              bucket.orders || []
                                                            )
                                                          }
                                                        />
                                                      ),
                                                      align: 'left',
                                                      width: 50,
                                                    },
                                                    { id: 'name', label: 'Order' },
                                                    { id: 'tracking', label: 'Tracking' },
                                                    { id: 'delivery', label: 'Delivery' },
                                                    { id: 'status', label: 'Status' },
                                                    { id: 'shelf', label: 'Shelf' },
                                                    { id: 'nusxa', label: 'Nusxa' },
                                                    { id: 'pallet', label: 'Pallet' },
                                                    { id: 'customs', label: 'Customs' },
                                                    { id: 'reciever', label: 'Primary Receiver' },
                                                    { id: 'orderActions', label: '' },
                                                  ]}
                                                />
                                                <TableBody>
                                                  {(bucket.orders || []).map((o) => (
                                                    <TableRow hover key={o.id}>
                                                      <TableCell padding="checkbox">
                                                        <Checkbox
                                                          size="small"
                                                          checked={selectedOrders.has(o.id)}
                                                          onChange={() => handleSelectOrder(o.id)}
                                                        />
                                                      </TableCell>
                                                      <TableCell sx={{ maxWidth: 280 }}>
                                                        <Stack spacing={0.5}>
                                                          <Box fontWeight={600}>{fmt(o.name)}</Box>
                                                          <Box color="text.secondary">
                                                            {fmt(o.room_number)}
                                                          </Box>
                                                        </Stack>
                                                      </TableCell>
                                                      <TableCell>
                                                        <Stack spacing={0.5}>
                                                          <Box>{fmt(o.tracking_number)}</Box>
                                                        </Stack>
                                                      </TableCell>
                                                      <TableCell>
                                                        {fmt(destinationDeliveryOf(o)?.replace(/_/g, ' '))}
                                                      </TableCell>
                                                      <TableCell>
                                                        <Chip
                                                          size="small"
                                                          label={fmt(o.status)}
                                                          sx={{ textTransform: 'capitalize' }}
                                                        />
                                                      </TableCell>
                                                      <TableCell>
                                                        <Typography variant="body2" noWrap>
                                                          {fmt(o.shelf?.name || o.shelf?.code)}
                                                        </Typography>
                                                      </TableCell>
                                                      <TableCell>
                                                        <Typography variant="body2" noWrap>
                                                          {fmt(o?.nusxa)}
                                                        </Typography>
                                                      </TableCell>
                                                      <TableCell>
                                                        <Typography variant="body2" noWrap>
                                                          {fmt(o.pallet?.tracking)}
                                                        </Typography>
                                                      </TableCell>
                                                      <TableCell>
                                                        <Typography variant="body2" noWrap>
                                                          {o.NeedsCustom !== true && fmt()}
                                                          {o.NeedsCustom &&
                                                            o?.custom_fee?.custum_fee_payed ===
                                                              true &&
                                                            fmt('Paid')}
                                                          {o.NeedsCustom &&
                                                            o?.custom_fee?.custum_fee_payed !==
                                                              true &&
                                                            fmt('Unpaid')}
                                                        </Typography>
                                                      </TableCell>
                                                      <TableCell>
                                                        <Stack spacing={0.5}>
                                                          <Box>{`${
                                                            o?.secondaryReceiver?.first_name ?? ''
                                                          } ${
                                                            o?.secondaryReceiver?.last_name ?? ''
                                                          }`}</Box>
                                                        </Stack>
                                                      </TableCell>
                                                      <TableCell align="right">
                                                        <Stack
                                                          direction="row"
                                                          spacing={1}
                                                          justifyContent="flex-end"
                                                        >
                                                          <Tooltip title="Open details">
                                                            <IconButton
                                                              size="small"
                                                              onClick={() => openOrderDrawer(o)}
                                                            >
                                                              <Iconify icon="mdi:eye" />
                                                            </IconButton>
                                                          </Tooltip>
                                                          <Tooltip title="Delete">
                                                            <IconButton
                                                              size="small"
                                                              color="error"
                                                              onClick={(e) =>
                                                                handleDeleteRow(e, o.id)
                                                              }
                                                            >
                                                              <Iconify icon="solar:trash-bin-trash-bold" />
                                                            </IconButton>
                                                          </Tooltip>
                                                        </Stack>
                                                      </TableCell>
                                                    </TableRow>
                                                  ))}

                                                  <TableEmptyRows
                                                    height={denseHeight}
                                                    emptyRows={emptyRows(0, 0, 0)}
                                                  />

                                                  {!bucket.orders?.length && (
                                                    <TableRow>
                                                      <TableCell
                                                        colSpan={9}
                                                        align="center"
                                                        sx={{ py: 3 }}
                                                      >
                                                        <TableNoData notFound />
                                                      </TableCell>
                                                    </TableRow>
                                                  )}
                                                </TableBody>
                                              </Table>
                                            </Box>
                                          </Collapse>
                                        </Box>
                                      );
                                    })
                                  ) : (
                                    // Fallback: old shape with `group.orders`
                                    <Table size="small" sx={{ minWidth: 1000 }}>
                                      <TableHeadCustom
                                        headLabel={[
                                          {
                                            id: 'select',
                                            label: (
                                              <Checkbox
                                                size="small"
                                                checked={
                                                  (group.orders || []).length > 0 &&
                                                  (group.orders || []).every((order) =>
                                                    selectedOrders.has(order.id)
                                                  )
                                                }
                                                indeterminate={
                                                  (group.orders || []).some((order) =>
                                                    selectedOrders.has(order.id)
                                                  ) &&
                                                  !(group.orders || []).every((order) =>
                                                    selectedOrders.has(order.id)
                                                  )
                                                }
                                                onChange={() =>
                                                  handleSelectAllInBucket(group.orders || [])
                                                }
                                              />
                                            ),
                                            align: 'left',
                                            width: 50,
                                          },
                                          { id: 'name', label: 'Order' },
                                          { id: 'tracking', label: 'Tracking' },
                                          { id: 'delivery', label: 'Delivery' },
                                          { id: 'status', label: 'Status' },
                                          { id: 'shelf', label: 'Shelf' },
                                          { id: 'reciever', label: 'Secondary Receiver' },
                                          { id: 'orderActions', label: '' },
                                        ]}
                                      />
                                      <TableBody>
                                        {(group.orders || []).map((o) => (
                                          <TableRow hover key={o.id}>
                                            <TableCell padding="checkbox">
                                              <Checkbox
                                                size="small"
                                                checked={selectedOrders.has(o.id)}
                                                onChange={() => handleSelectOrder(o.id)}
                                              />
                                            </TableCell>
                                            <TableCell sx={{ maxWidth: 280 }}>
                                              <Stack spacing={0.5}>
                                                <Box fontWeight={600}>{fmt(o.name)}</Box>
                                                <Box color="text.secondary">
                                                  {fmt(o.room_number)}
                                                </Box>
                                              </Stack>
                                            </TableCell>
                                            <TableCell>
                                              <Stack spacing={0.5}>
                                                <Box>{fmt(o.tracking_number)}</Box>
                                              </Stack>
                                            </TableCell>
                                            <TableCell>
                                              {fmt(destinationDeliveryOf(o)?.replace(/_/g, ' '))}
                                            </TableCell>
                                            <TableCell>
                                              <Chip
                                                size="small"
                                                label={fmt(o.status)}
                                                sx={{ textTransform: 'capitalize' }}
                                              />
                                            </TableCell>
                                            <TableCell>
                                              <Typography variant="body2" noWrap>
                                                {o.custom_status === '8' ? fmt(o.shelf?.code) : '-'}
                                              </Typography>
                                            </TableCell>
                                            <TableCell>
                                              <Stack spacing={0.5}>
                                                <Box>{`${o?.secondaryReceiver?.first_name ?? ''} ${
                                                  o?.secondaryReceiver?.last_name ?? ''
                                                }`}</Box>
                                              </Stack>
                                            </TableCell>
                                            <TableCell align="right">
                                              <Stack
                                                direction="row"
                                                spacing={1}
                                                justifyContent="flex-end"
                                              >
                                                <Tooltip title="Print Label">
                                                  <IconButton
                                                    size="small"
                                                    color="primary"
                                                    onClick={() => handlePrintLabel(o)}
                                                  >
                                                    <Iconify icon="mdi:printer" />
                                                  </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Open details">
                                                  <IconButton
                                                    size="small"
                                                    onClick={() => openOrderDrawer(o)}
                                                  >
                                                    <Iconify icon="mdi:eye" />
                                                  </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Delete">
                                                  <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={(e) => handleDeleteRow(e, o.id)}
                                                  >
                                                    <Iconify icon="solar:trash-bin-trash-bold" />
                                                  </IconButton>
                                                </Tooltip>
                                              </Stack>
                                            </TableCell>
                                          </TableRow>
                                        ))}

                                        <TableEmptyRows
                                          height={denseHeight}
                                          emptyRows={emptyRows(0, 0, 0)}
                                        />

                                        {!group.orders?.length && (
                                          <TableRow>
                                            <TableCell colSpan={9} align="center" sx={{ py: 3 }}>
                                              <TableNoData notFound />
                                            </TableCell>
                                          </TableRow>
                                        )}
                                      </TableBody>
                                    </Table>
                                  )}
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        </Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              ) : null}

              {!groups.length && (
                <TableRow
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <TableCell>
                    <TableNoData notFound={notFound} sx={{ width: '800px' }} />
                  </TableCell>
                </TableRow>
              )}
            </Scrollbar>
          </TableContainer>

          <TablePaginationCustom
            count={tableDetails?.total ? tableDetails.total : 0}
            page={tableDetails?.page ? tableDetails.page - 1 : 0}
            rowsPerPage={tableDetails?.take ? tableDetails.take : 25}
            onPageChange={(event, newPage) => {
              setFilters((prev) => ({ ...prev, page: newPage + 1 }));
            }}
            onRowsPerPageChange={(event) => {
              const { value } = event.target;
              setFilters((prev) => ({
                ...prev,
                take: parseInt(value, 10),
                page: 1,
              }));
            }}
            dense={table.dense}
            onChangeDense={table.onChangeDense}
          />
        </Card>
      </Container>

      <ConfirmDialog
        open={confirm.value}
        onClose={() => {
          setPendingDeleteId(null);
          confirm.onFalse();
        }}
        title="Delete"
        content="Are you sure you want to delete this order?"
        action={
          <Button
            variant="contained"
            color="error"
            disabled={!pendingDeleteId}
            onClick={async () => {
              const deleted = await deletePhysicalOrder(pendingDeleteId);
              if (deleted) await getOrderssPhysical(filters);
              if (deleted && aRow?.id === pendingDeleteId) {
                // close drawer if it's the same order
                setIsDrawerOpen(false);
                setARow({});
              }
              setPendingDeleteId(null);
              confirm.onFalse();
            }}
          >
            Delete
          </Button>
        }
      />

      <Dialog
        open={customFeeDialogOpen}
        onClose={() => {
          setCustomFeeDialogOpen(false);
          setCustomFeeData(null);
          setCurrentGroupId(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            Custom Fee Summary
            {customFeeData?.isPaid && (
              <Chip
                icon={<Iconify icon="mdi:check-circle" />}
                label="Paid"
                color="success"
                size="small"
              />
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          {(() => {
            if (customFeeLoading) {
              return (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                  <LinearProgress sx={{ width: '100%' }} />
                </Box>
              );
            }

            if (customFeeData) {
              return (
                <Stack spacing={2} sx={{ mt: 1 }}>
                  {customFeeData.isPaid && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Iconify
                        icon="mdi:check-circle"
                        sx={{ color: 'success.main', fontSize: 24 }}
                      />
                      <Typography variant="body1" color="success.main" sx={{ fontWeight: 600 }}>
                        All custom fees have been paid
                      </Typography>
                    </Box>
                  )}
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Custom Fee Amount
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {customFeeData.customFeeSum?.toFixed(2) || '0.00'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Service Fee Amount
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {customFeeData.serviceFeeSum?.toFixed(2) || '0.00'}
                    </Typography>
                  </Box>
                  <Divider />
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Total Amount
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main' }}>
                      {customFeeData.totalSum?.toFixed(2) || '0.00'}
                    </Typography>
                  </Box>
                </Stack>
              );
            }

            return (
              <Typography variant="body2" color="error">
                No custom fee data available
              </Typography>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setCustomFeeDialogOpen(false);
              setCustomFeeData(null);
              setCurrentGroupId(null);
            }}
            disabled={payingCustomFee}
          >
            Close
          </Button>
          {customFeeData && customFeeData.totalSum > 0 && !customFeeData.isPaid && (
            <Button
              variant="contained"
              color="primary"
              onClick={handlePayCustomFees}
              disabled={payingCustomFee}
              startIcon={<Iconify icon="mdi:cash-multiple" />}
            >
              {payingCustomFee ? 'Processing...' : 'Pay Custom Fees'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </div>
  );
}

TcTable.propTypes = {
  STATUS_OPTIONS: PropTypes.array,
  TABLE_HEAD: PropTypes.array,
  defaultFilters: PropTypes.object,
  tableNames: PropTypes.array,
  OTHER_STATUS_OPTIONS_FOR_ORDER: PropTypes.array,
  TIME_SLOT_FOR_ORDER: PropTypes.array,
};
